"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import { cn } from "@/utils/utils";

export default function CategorizingWordsExercise({
  exercise = {},
  onUpdate,           // present in edit mode
  errors = {},
  isPreview = false,  // false = edit, true = student
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= RAW DATA (EDIT MODE) ================= */
  const rawCategories = Array.isArray(exercise.categories)
    ? exercise.categories
    : [];

  const rawWords = Array.isArray(exercise.words)
    ? exercise.words
    : [];

  /* ================= SAFE DATA (PREVIEW MODE) ================= */
  const previewCategories = rawCategories.filter(Boolean);

  const previewWords = rawWords.filter(
    w => w?.word?.trim() && w?.category?.trim()
  );

  const categories = isEditMode ? rawCategories : previewCategories;
  const words = isEditMode ? rawWords : previewWords;

  /* ================= STUDENT STATE ================= */
  const [wordBank, setWordBank] = useState([]);
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= INIT WORD BANK ================= */
  useEffect(() => {
    if (!isPreview) return;

    const shuffled = [...words.map(w => w.word)].sort(
      () => Math.random() - 0.5
    );
    setWordBank(shuffled);
  }, [isPreview, words]);

  /* ================= HELPERS ================= */
  const updateField = (field, value) => {
    if (isEditMode) onUpdate(field, value);
  };

  /* ================= STUDENT DROP ================= */
  const handleDrop = (e, category) => {
    if (!isPreview) return;
    e.preventDefault();

    const word = e.dataTransfer.getData("text/plain");
    if (!word || !wordBank.includes(word)) return;

    const newAnswers = { ...answers, [word]: category };
    const newBank = wordBank.filter(w => w !== word);

    setAnswers(newAnswers);
    setWordBank(newBank);

    if (newBank.length === 0) checkAnswers(newAnswers);
  };

  const checkAnswers = (finalAnswers) => {
    if (completedRef.current) return;
    completedRef.current = true;

    const allCorrect = words.every(
      w =>
        finalAnswers[w.word]?.toLowerCase().trim() ===
        w.category.toLowerCase().trim()
    );

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {/* ===== Categories ===== */}
        <div>
          <p className="font-medium">Categories</p>

          {categories.map((cat, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <StringField
                value={cat}
                onChange={v => {
                  const next = [...categories];
                  next[i] = v;
                  updateField("categories", next);
                }}
                placeholder="e.g. Fruits"
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  updateField(
                    "categories",
                    categories.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            variant="outline"
            className="mt-3"
            onClick={() =>
              updateField("categories", [...categories, ""])
            }
          >
            Add Category
          </Button>
        </div>

        {/* ===== Words ===== */}
        <div>
          <p className="font-medium">Words</p>

          {words.map((w, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <StringField
                value={w.word}
                placeholder="Word"
                onChange={v => {
                  const next = [...words];
                  next[i] = { ...next[i], word: v };
                  updateField("words", next);
                }}
              />

              <select
                value={w.category}
                onChange={e => {
                  const next = [...words];
                  next[i] = { ...next[i], category: e.target.value };
                  updateField("words", next);
                }}
                className="border rounded px-3 py-2"
              >
                <option value="">Select category</option>
                {categories.filter(Boolean).map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  updateField(
                    "words",
                    words.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            variant="outline"
            className="mt-3"
            onClick={() =>
              updateField("words", [
                ...words,
                { word: "", category: "" },
              ])
            }
          >
            Add Word
          </Button>
        </div>
      </div>
    );
  }

  /* ================= PREVIEW MODE ================= */
  return (
    <Card>
      <CardHeader>
        <CardDescription>
          Drag each word into the correct category.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Word Bank */}
        <div className="flex flex-wrap gap-3 p-4 border rounded-lg bg-muted justify-center min-h-[80px]">
          {wordBank.length ? (
            wordBank.map(word => (
              <Button
                key={word}
                draggable
                variant="secondary"
                onDragStart={e =>
                  e.dataTransfer.setData("text/plain", word)
                }
              >
                {word}
              </Button>
            ))
          ) : (
            <p className="text-muted-foreground">
              All words placed!
            </p>
          )}
        </div>

        {/* Categories */}
        <div className="grid md:grid-cols-2 gap-6">
          {categories.map(cat => (
            <div
              key={cat}
              onDrop={e => handleDrop(e, cat)}
              onDragOver={e => e.preventDefault()}
              className="border-2 border-dashed rounded-xl p-6 min-h-[200px]"
            >
              <h3 className="font-bold text-center mb-4 text-lg">
                {cat}
              </h3>

              <div className="space-y-2">
                {Object.entries(answers)
                  .filter(([, c]) => c === cat)
                  .map(([word]) => {
                    const correct =
                      words.find(w => w.word === word)?.category === cat;

                    return (
                      <div
                        key={word}
                        className={cn(
                          "p-3 rounded text-center font-medium",
                          showResult && correct && "bg-green-100 text-green-800",
                          showResult && !correct && "bg-red-100 text-red-800"
                        )}
                      >
                        {word}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
